import { useEffect, useMemo, useRef, useState } from 'react';

import { Box, CircularProgress, Typography } from '@mui/material';
import { format, isValid } from 'date-fns';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { AppPage } from '@/shared/components';
import { useUserStateContext } from '@/shared/components/UserStateProvider';
import { useDocumentTitle } from '@/shared/hooks/useDocumentTitle';
import { cx } from '@/styles/muiTheme';

import useClipLibraryFacetsQuery, {
  ClipFacetGame,
  ClipFacetPlayer,
  ClipFacetSeason,
} from '../../hooks/useClipLibraryFacetsQuery';
// Aliased: the sibling component of the same name is the filter *bar*, this is
// the value object it drives.
import useClipLibraryQuery, { ClipLibraryFilters as LibraryFilterValues } from '../../hooks/useClipLibraryQuery';
import { getEventLabel } from '../../services/clipEventStyle';
import toClipLibraryItems, {
  ClipLibraryItem,
  ClipMatchupTeam,
  seasonLabelOf,
  teamColorOf,
} from '../../services/toClipLibraryItems';
import ClipCard from './ClipCard';
import ClipLibraryFilters, { CreatorFilter, FilterOption, GroupMode } from './ClipLibraryFilters';
import ClipPlayerDialog from './ClipPlayerDialog';
import type { GameFilterOption } from './GameSelect';
import PlayerAvatar from './PlayerAvatar';
import type { PlayerFilterOption } from './PlayerSelect';
import TeamCrest from './TeamCrest';

const TITLE = 'Clip Library';

/** Sentinel for every picker's "no filter" position. */
const ALL = 'all';

/**
 * Long enough that typing a word is one request rather than five, short enough
 * that the grid still feels live.
 */
const SEARCH_DEBOUNCE_MS = 300;

type ClipGroup = {
  key: string;
  title: string;
  subtitle: string;
  /** Crest pair, set only when grouping by game. */
  matchup?: { away: ClipMatchupTeam; home: ClipMatchupTeam };
  /** Portrait, set only when grouping by tracked player. */
  headshotUrl?: string | null;
  clips: ClipLibraryItem[];
};

const pluralize = (count: number) => `${count} clip${count === 1 ? '' : 's'}`;

const formatGameDate = (iso: string): string => {
  const parsed = new Date(iso);
  return isValid(parsed) ? format(parsed, 'MM/dd/yy') : '';
};

const toMatchupTeam = (team: ClipFacetGame['homeTeam']): ClipMatchupTeam => ({
  abbreviation: team?.abbreviation || '',
  logoUrl: team?.logoUrl || null,
});

const toSeasonOption = (season: ClipFacetSeason): FilterOption => ({
  value: String(season.id),
  label: seasonLabelOf(season) || 'No season',
});

const toGameOption = (
  game: ClipFacetGame,
  seasonLabelById: Map<number, string>,
): GameFilterOption => {
  const away = toMatchupTeam(game.awayTeam);
  const home = toMatchupTeam(game.homeTeam);
  const gameDate = formatGameDate(game.startTimestamp);
  const title =
    away.abbreviation && home.abbreviation
      ? `${away.abbreviation} vs ${home.abbreviation}`
      : 'Unknown game';

  return {
    value: String(game.id),
    label: [title, gameDate].filter(Boolean).join(' · '),
    gameDate,
    seasonLabel: (game.seasonId && seasonLabelById.get(game.seasonId)) || 'No season',
    matchup: { away, home },
  };
};

const toPlayerOption = (player: ClipFacetPlayer): PlayerFilterOption => ({
  value: String(player.id),
  label: `#${player.playerNumber} ${player.displayName}`.trim(),
  playerNumber: player.playerNumber,
  playerName: player.displayName,
  playerPosition: player.playerPosition,
  headshotUrl: player.headshot ?? null,
  teamAbbreviation: player.teamAbbreviation ?? '',
  teamLogoUrl: player.teamLogoUrl ?? null,
  teamColor: teamColorOf({ teamColors: player.teamColors }),
});

const ClipLibraryPage = () => {
  const [mode, setMode] = useState<GroupMode>('game');
  const [player, setPlayer] = useState(ALL);
  const [season, setSeason] = useState(ALL);
  const [game, setGame] = useState(ALL);
  const [event, setEvent] = useState(ALL);
  const [creator, setCreator] = useState<CreatorFilter>(ALL);
  /** What the box shows. `debouncedQuery` is what actually gets queried. */
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  /** The clip open in the player; null when the dialog is closed. */
  const [activeClip, setActiveClip] = useState<ClipLibraryItem | null>(null);

  useDocumentTitle(TITLE);

  const { currentLeague } = useUserStateContext();
  const { zeroTouchClipping } = useFlags();

  // Automated clips ride the same shadow launch as the game page's Automated
  // Clips tab (C360-2813): until zeroTouchClipping is on they are not the
  // user's to see. Applied server-side, so they never reach the browser at all
  // — and the facets carry the same flag, so a clip that cannot be listed
  // cannot put a player or a game into a dropdown either.
  const excludeAutomated = !zeroTouchClipping;

  // The automation option only exists while the flag is on, so a selection left
  // over from before it turned off falls back to 'all' instead of emptying the
  // grid with no visible cause.
  const effectiveCreator: CreatorFilter =
    creator === 'automation' && !zeroTouchClipping ? ALL : creator;

  // Typing re-queries, so it waits for a pause. Everything else is a discrete
  // choice and applies at once.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  // Every filter is resolved by the API, so this object is the query: change any
  // field and react-query starts a fresh paginated sequence. That is what lifts
  // the old one-page cap — the grid is no longer limited to what was fetched.
  const filters = useMemo<LibraryFilterValues>(
    () => ({
      leagueId: currentLeague?.id,
      seasonId: season === ALL ? undefined : Number(season),
      gameId: game === ALL ? undefined : Number(game),
      playerId: player === ALL ? undefined : Number(player),
      eventType: event === ALL ? undefined : event,
      creator: effectiveCreator === ALL ? undefined : effectiveCreator,
      text: debouncedQuery || undefined,
      excludeAutomated,
    }),
    [currentLeague?.id, season, game, player, event, effectiveCreator, debouncedQuery, excludeAutomated],
  );

  const clipsQuery = useClipLibraryQuery(filters);
  const facetsQuery = useClipLibraryFacetsQuery(currentLeague?.id, excludeAutomated);

  const facets = facetsQuery.data;

  const clips = useMemo(
    () => toClipLibraryItems(clipsQuery.data?.pages.flatMap((page) => page.rows) ?? []),
    [clipsQuery.data],
  );

  // The count the filter bar shows is the server's total for these filters, not
  // the number loaded so far — otherwise it would climb as the user scrolls.
  const total = clipsQuery.data?.pages[0]?.rowCount ?? 0;

  const seasonLabelById = useMemo(
    () => new Map((facets?.seasons ?? []).map((s) => [s.id, seasonLabelOf(s) || 'No season'])),
    [facets?.seasons],
  );

  const seasonOptions = useMemo(
    () => (facets?.seasons ?? []).map(toSeasonOption),
    [facets?.seasons],
  );

  // A game belongs to exactly one season, so the game list narrows to the chosen
  // one. Player and event span seasons and are left alone.
  const gameOptions = useMemo(
    () =>
      (facets?.games ?? [])
        .filter((g) => season === ALL || String(g.seasonId) === season)
        .map((g) => toGameOption(g, seasonLabelById)),
    [facets?.games, season, seasonLabelById],
  );

  const playerOptions = useMemo(
    () => (facets?.players ?? []).map(toPlayerOption),
    [facets?.players],
  );

  const eventOptions = useMemo<FilterOption[]>(
    () =>
      (facets?.eventTypes ?? [])
        .map((value) => ({ value, label: getEventLabel(value) }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [facets?.eventTypes],
  );

  const groups = useMemo<ClipGroup[]>(() => {
    if (mode === 'all') {
      return clips.length ? [{ key: 'all', title: '', subtitle: '', clips }] : [];
    }

    const keyOf = (clip: ClipLibraryItem) =>
      mode === 'game' ? clip.gameKey : mode === 'player' ? clip.playerKey : clip.eventKey;

    const buckets = new Map<string, ClipLibraryItem[]>();
    clips.forEach((clip) => {
      const key = keyOf(clip);
      const bucket = buckets.get(key);
      if (bucket) bucket.push(clip);
      else buckets.set(key, [clip]);
    });

    const built = [...buckets].map(([key, items]) => {
      const [first] = items;
      const count = pluralize(items.length);

      if (mode === 'game') {
        return {
          key,
          title: first.gameTitle,
          subtitle: [first.gameDate, first.seasonLabel, count].filter(Boolean).join(' · '),
          matchup: first.matchup,
          clips: items,
        };
      }
      if (mode === 'player') {
        return {
          key,
          title: first.playerTitle,
          subtitle: [first.playerSubtitle, count].filter(Boolean).join(' · '),
          headshotUrl: items.find((item) => item.playerHeadshotUrl)?.playerHeadshotUrl ?? null,
          clips: items,
        };
      }
      return { key, title: first.eventLabel, subtitle: count, clips: items };
    });

    // Games read newest-first; the other two are unordered sets, so size is the
    // most useful ordering, with the label breaking ties deterministically.
    // Counts here are per-loaded-page, not per-filter — a later page can move a
    // group, which is the honest behaviour for a list that is still arriving.
    return mode === 'game'
      ? built.sort((a, b) => a.clips[0].gameSortKey - b.clips[0].gameSortKey)
      : built.sort((a, b) => b.clips.length - a.clips.length || a.title.localeCompare(b.title));
  }, [clips, mode]);

  // Switching league retires the season list, and with it any season — and so
  // any game — the user had picked out of the old one.
  useEffect(() => {
    setSeason(ALL);
    setGame(ALL);
  }, [currentLeague?.id]);

  // Changing season invalidates any selected game, so clear it rather than
  // leaving the grid in an impossible season+game combination.
  const handleSeasonChange = (next: string) => {
    setSeason(next);
    setGame(ALL);
  };

  const resetFilters = () => {
    setPlayer(ALL);
    setSeason(ALL);
    setGame(ALL);
    setEvent(ALL);
    setCreator(ALL);
    setQuery('');
  };

  const hasActiveFilters =
    player !== ALL || season !== ALL || game !== ALL || event !== ALL || creator !== ALL || Boolean(query);

  const { hasNextPage, isFetchingNextPage, fetchNextPage } = clipsQuery;
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  // Load-more on approach rather than on arrival: the 400px margin starts the
  // next page while the last row is still below the fold, so scrolling does not
  // stall. react-query drops a fetchNextPage that is already in flight, so the
  // observer firing repeatedly is harmless.
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasNextPage) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) fetchNextPage();
      },
      { rootMargin: '400px' },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [hasNextPage, fetchNextPage, groups.length]);

  // A filter change keeps the previous rows on screen (placeholderData), so the
  // grid dims instead of collapsing to an empty state and back. Paging in more
  // is not a refilter, so it must not dim.
  const isRefiltering = clipsQuery.isFetching && !isFetchingNextPage && !clipsQuery.isLoading;

  return (
    <AppPage title={TITLE} requiredPermissions={['menu:clips']}>
      <Box
        sx={{
          minHeight: '100%',
          backgroundColor: cx.bg.base,
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          padding: '8px',
        }}
      >
        <ClipLibraryFilters
          mode={mode}
          onModeChange={setMode}
          player={player}
          onPlayerChange={setPlayer}
          playerOptions={playerOptions}
          season={season}
          onSeasonChange={handleSeasonChange}
          seasonOptions={seasonOptions}
          game={game}
          onGameChange={setGame}
          gameOptions={gameOptions}
          creator={creator}
          onCreatorChange={setCreator}
          showAutomationCreator={Boolean(zeroTouchClipping)}
          event={event}
          onEventChange={setEvent}
          eventOptions={eventOptions}
          query={query}
          onQueryChange={setQuery}
          countLabel={pluralize(total)}
        />

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
            padding: '8px 4px 32px',
            opacity: isRefiltering ? 0.5 : 1,
            transition: 'opacity 120ms',
          }}
        >
          {clipsQuery.isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
              <CircularProgress size={28} />
            </Box>
          ) : null}

          {clipsQuery.isError ? (
            <Typography sx={{ textAlign: 'center', padding: '48px', color: cx.red.main, fontSize: 14 }}>
              An error occurred while fetching clips.
            </Typography>
          ) : null}

          {!clipsQuery.isLoading && !clipsQuery.isError && !groups.length ? (
            <Box
              sx={{
                backgroundColor: cx.bg.surface3,
                borderRadius: '4px',
                padding: '48px',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                alignItems: 'center',
              }}
            >
              {/* Keyed off the filters rather than the result: with none set
                  there is nothing for "clear filters" to undo, so the league
                  simply has no clips. */}
              <Typography sx={{ fontSize: 14, color: cx.text.secondary }}>
                {hasActiveFilters ? 'No clips match these filters' : 'No clips yet'}
              </Typography>
              {hasActiveFilters ? (
                <Box
                  component="button"
                  type="button"
                  onClick={resetFilters}
                  sx={{
                    height: 32,
                    padding: '0 16px',
                    border: 'none',
                    borderRadius: '100px',
                    backgroundColor: cx.blue.main,
                    color: cx.text.primary,
                    fontSize: 12,
                    cursor: 'pointer',
                  }}
                >
                  Clear filters
                </Box>
              ) : null}
            </Box>
          ) : null}

          {groups.map((group) => (
            <Box key={group.key} sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {group.title ? (
                <Box
                  sx={{
                    display: 'flex',
                    // Centre, not baseline: the crests are images and have no
                    // useful text baseline to sit on.
                    alignItems: 'center',
                    gap: '12px',
                    borderBottom: `1px solid ${cx.border.subtle}`,
                    paddingBottom: '8px',
                  }}
                >
                  {group.matchup ? (
                    <TeamCrest
                      abbreviation={group.matchup.away.abbreviation}
                      logoUrl={group.matchup.away.logoUrl}
                      size={24}
                    />
                  ) : null}
                  {mode === 'player' ? (
                    <PlayerAvatar name={group.title} headshotUrl={group.headshotUrl ?? null} size={28} />
                  ) : null}
                  <Typography sx={{ fontSize: 18, lineHeight: '26px', letterSpacing: '0.15px', color: cx.text.primary }}>
                    {group.title}
                  </Typography>
                  {group.matchup ? (
                    <TeamCrest
                      abbreviation={group.matchup.home.abbreviation}
                      logoUrl={group.matchup.home.logoUrl}
                      size={24}
                    />
                  ) : null}
                  <Typography sx={{ fontSize: 11, color: cx.text.tertiary, letterSpacing: '0.15px' }}>
                    {group.subtitle}
                  </Typography>
                </Box>
              ) : null}

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                  gap: '24px 16px',
                }}
              >
                {group.clips.map((clip) => (
                  <ClipCard key={clip.id} clip={clip} onOpen={() => setActiveClip(clip)} />
                ))}
              </Box>
            </Box>
          ))}

          {hasNextPage ? (
            <Box
              ref={sentinelRef}
              sx={{ display: 'flex', justifyContent: 'center', padding: '16px' }}
            >
              {isFetchingNextPage ? (
                <CircularProgress size={20} />
              ) : (
                // The observer normally gets there first; the button is the
                // fallback for a viewport too tall to ever scroll it into view.
                <Box
                  component="button"
                  type="button"
                  onClick={() => fetchNextPage()}
                  sx={{
                    height: 32,
                    padding: '0 16px',
                    border: `1px solid ${cx.border.strong}`,
                    borderRadius: '100px',
                    backgroundColor: cx.bg.surface2,
                    color: cx.text.secondary,
                    fontSize: 12,
                    cursor: 'pointer',
                  }}
                >
                  Load more
                </Box>
              )}
            </Box>
          ) : null}
        </Box>
      </Box>

      <ClipPlayerDialog clip={activeClip} onClose={() => setActiveClip(null)} />
    </AppPage>
  );
};

export default ClipLibraryPage;
